import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { documentTypeApi, managementCompanyApi } from "@/services/api";
import { cmsApi } from "@/services/api/cmsApi";
import { useToast } from "@/hooks/use-toast";
import type { CMSContent } from "@/models/CMS";

/**
 * Settings for a top-level content folder (8-Sep): which document type its
 * templates are an output for — so the template pickers can't offer "un
 * template que no tiene nada que ver" — and whose folder it is (platform or
 * one management company).
 */
const FolderSettings = ({ content }: { content: CMSContent }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [documentTypeId, setDocumentTypeId] = useState<number | null>(
    content.document_type_id ?? null
  );
  const [managementCompanyId, setManagementCompanyId] = useState<number | null>(
    content.management_company_id ?? null
  );
  const [saving, setSaving] = useState(false);

  const { data: documentTypes = [] } = useQuery({
    queryKey: ["documentTypes"],
    queryFn: () => documentTypeApi.list(),
  });
  const { data: managementCompanies = [] } = useQuery({
    queryKey: ["managementCompanies"],
    queryFn: () => managementCompanyApi.list(),
  });

  const dirty =
    (documentTypeId ?? null) !== (content.document_type_id ?? null) ||
    (managementCompanyId ?? null) !== (content.management_company_id ?? null);

  const save = async () => {
    try {
      setSaving(true);
      await cmsApi.updateContent(content.id, {
        document_type_id: documentTypeId,
        management_company_id: managementCompanyId,
      } as Partial<CMSContent>);
      queryClient.invalidateQueries({ queryKey: ["cms-content"] });
      queryClient.invalidateQueries({ queryKey: ["templateFolders"] });
      toast({ title: "Folder settings saved" });
    } catch (err: any) {
      toast({
        title: "Error saving folder settings",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <CardContent className="flex flex-wrap items-end gap-4 pt-4">
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            Compatible document type
          </div>
          <Select
            value={documentTypeId != null ? String(documentTypeId) : "generic"}
            onValueChange={(v) =>
              setDocumentTypeId(v === "generic" ? null : Number(v))
            }
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="generic">
                Generic — offered everywhere
              </SelectItem>
              {documentTypes.map((dt: any) => (
                <SelectItem key={dt.id} value={String(dt.id)}>
                  {dt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">Scope</div>
          <Select
            value={
              managementCompanyId != null
                ? String(managementCompanyId)
                : "platform"
            }
            onValueChange={(v) =>
              setManagementCompanyId(v === "platform" ? null : Number(v))
            }
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="platform">
                Platform (all management companies)
              </SelectItem>
              {managementCompanies.map((mc: any) => (
                <SelectItem key={mc.id} value={String(mc.id)}>
                  {mc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save folder settings"}
        </Button>
        <p className="w-full text-xs text-muted-foreground">
          The template pickers only offer folders compatible with the
          document's type (generic folders show everywhere). Scoped folders
          belong to one management company.
        </p>
      </CardContent>
    </Card>
  );
};

export default FolderSettings;
